/**
 * SavedMessagesPanel — Vista messaggi salvati (cross-chat)
 * Notion-style design
 */

import React, { useEffect, useState } from 'react';
import { X, Bookmark, ArrowRight, Loader2 } from 'lucide-react';
import { useTeamChatStore, TeamChatMessage } from '../../lib/stores/teamChatStore';
import { formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';

interface SavedMessagesPanelProps {
  onClose: () => void;
  onNavigateToMessage?: (chatId: string, messageId: string) => void;
}

export function SavedMessagesPanel({ onClose, onNavigateToMessage }: SavedMessagesPanelProps) {
  const { getSavedMessages, unsaveMessage } = useTeamChatStore();
  const [messages, setMessages] = useState<(TeamChatMessage & { chat_name?: string })[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getSavedMessages().then(msgs => {
      if (!cancelled) {
        setMessages(msgs);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [getSavedMessages]);

  const truncate = (text: string, max = 150) => {
    if (!text) return '';
    try {
      const parsed = JSON.parse(text);
      if (parsed.entity_data?.name) return `📎 ${parsed.entity_data.name}`;
      if (parsed.comment_content) return `💬 ${parsed.comment_content}`;
      if (parsed.title) return parsed.title;
    } catch { /* plain text */ }
    return text.length > max ? text.slice(0, max) + '…' : text;
  };

  const handleUnsave = async (messageId: string) => {
    await unsaveMessage(messageId);
    setMessages(prev => prev.filter(m => m.id !== messageId));
  };

  return (
    <div
      className="flex flex-col h-full w-full"
      style={{ backgroundColor: '#FBFBFA' }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-3 py-2.5 flex-shrink-0"
        style={{ borderBottom: '1px solid rgba(55,53,47,0.09)' }}
      >
        <div className="flex items-center gap-1.5">
          <Bookmark className="w-3.5 h-3.5" style={{ color: '#2383E2' }} strokeWidth={1.5} />
          <span className="text-[13px] font-semibold" style={{ color: '#37352F' }}>
            Messaggi salvati
          </span>
          {messages.length > 0 && (
            <span className="text-[11px] px-1.5 rounded-full" style={{ backgroundColor: 'rgba(35,131,226,0.1)', color: '#2383E2' }}>
              {messages.length}
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-[rgba(55,53,47,0.06)] transition-colors"
        >
          <X className="w-3.5 h-3.5" style={{ color: '#787774' }} strokeWidth={1.5} />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-2">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#2383E2' }} />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center px-4">
            <Bookmark className="w-8 h-8 mb-2" style={{ color: '#D3D1CB' }} strokeWidth={1} />
            <p className="text-[13px]" style={{ color: '#9B9A97' }}>Nessun messaggio salvato</p>
            <p className="text-[11px] mt-1" style={{ color: '#D3D1CB' }}>
              Usa il bottone salva sui messaggi per ritrovarli qui
            </p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {messages.map(msg => (
              <div
                key={msg.id}
                className="rounded-lg px-3 py-2.5 transition-colors group"
                style={{ backgroundColor: 'white', border: '1px solid rgba(55,53,47,0.06)' }}
              >
                {/* Chat name */}
                {msg.chat_name && (
                  <div className="text-[10px] font-medium mb-1" style={{ color: '#2383E2' }}>
                    {msg.chat_name}
                  </div>
                )}
                {/* Author + time */}
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] font-semibold" style={{ color: '#37352F' }}>
                    {msg.sender?.full_name || 'Sistema'}
                  </span>
                  <span className="text-[10px]" style={{ color: '#9B9A97' }}>
                    {formatDistanceToNow(new Date(msg.created_at), { addSuffix: false, locale: it })}
                  </span>
                </div>
                {/* Content */}
                <p className="text-[12px] leading-relaxed line-clamp-3 mb-1.5" style={{ color: '#787774' }}>
                  {truncate(msg.content)}
                </p>
                {/* Actions */}
                <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => onNavigateToMessage?.(msg.chat_id, msg.id)}
                    className="flex items-center gap-1 text-[10px] hover:underline"
                    style={{ color: '#2383E2' }}
                  >
                    Vai al messaggio <ArrowRight className="w-2.5 h-2.5" strokeWidth={2} />
                  </button>
                  <button
                    onClick={() => handleUnsave(msg.id)}
                    className="flex items-center gap-1 text-[10px] hover:underline"
                    style={{ color: '#9B9A97' }}
                  >
                    Rimuovi
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
